// Renaming and dish naming (§11.14). Pure.

/** NFD-normalize, strip marks, spaces → _, keep [A-Za-z0-9_-], collapse repeated _. */
export function sanitizeName(input: string): string {
  return input
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export type RenameContext = {
  orig: string; // original file name without extension
  seq: number; // 1-based position in export order
  client?: string;
  event?: string;
  date?: string; // YYYY-MM-DD from EXIF, if any
  menu?: string; // dish name assigned to this photo, if any
  preset?: string;
};

/** Expands `{client}` `{event}` `{seq:3}` `{orig}` `{date}` `{menu}` `{preset}`. Empty result falls back to `{orig}`. */
export function applyTemplate(template: string, ctx: RenameContext): string {
  const out = template.replace(/\{(\w+)(?::(\d+))?\}/g, (_m, key: string, width?: string) => {
    switch (key) {
      case 'seq':
        return String(ctx.seq).padStart(width ? Number(width) : 1, '0');
      case 'orig':
        return ctx.orig;
      case 'client':
        return ctx.client ?? '';
      case 'event':
        return ctx.event ?? '';
      case 'date':
        return ctx.date ?? '';
      case 'menu':
        return ctx.menu ?? '';
      case 'preset':
        return ctx.preset ?? '';
      default:
        return '';
    }
  });
  // Sanitize every part that came from user text; the original name is kept as-is (it is already a valid file name).
  const clean = template === '{orig}' ? out : sanitizeName(out);
  return clean || ctx.orig;
}

/** Makes names unique within one batch: second "Pollo" becomes "Pollo_2", and so on. */
export function dedupeNames(names: string[]): string[] {
  const seen = new Map<string, number>();
  return names.map((n) => {
    const key = n.toLowerCase();
    const k = (seen.get(key) ?? 0) + 1;
    seen.set(key, k);
    return k === 1 ? n : `${n}_${k}`;
  });
}

/** Assigns pasted menu lines (one dish per line) to photos in order. Blank lines are skipped. */
export function assignDishNames(menuText: string, count: number): (string | undefined)[] {
  const lines = menuText
    .split(/\r?\n/)
    .map((l) => sanitizeName(l))
    .filter(Boolean);
  return Array.from({ length: count }, (_, i) => lines[i]);
}

export const stripExt = (name: string) => name.replace(/\.[^.]+$/, '');
